import { useContext } from 'react';

import clsx from 'clsx';
import dynamic from 'next/dynamic';

const InlineForm = dynamic(
  () => import('bx-forms/eas/getapp').then((mod) => mod.InlineForm),
  { ssr: false }
);
import { Container } from 'components/Grid';
import LazyContent from 'components/LazyContent';
import ListingScreenshots from 'components/Listing/components/Screenshots';
import Paper from 'components/Paper';
import SoftwareApplicationLD from 'components/SoftwareApplicationLD/SoftwareApplicationLD';
import TrustBanner from 'components/TrustBanner';
import Typography from 'components/Typography';
import UserVerdict from 'components/UserVerdict';
import { useAppContext } from 'context/AppContext';
import { CompareContext } from 'context/CompareContext';
import { EventContext } from 'context/EventContext';
import { getVideoStructuredData } from 'helpers/SEOHelper';

import { SECTIONS, generateNavsForUserVerdicts } from '../../constants';
import AddToCompare from '../AddToCompare';
import FeaturesIntegrations from '../FeaturesIntegrations';
import GridContainer from '../GridContainer';
import GridItem from '../GridItem';
import ListingTopHeader from '../ListingTopHeader';
import ListingVideos from '../ListingVideos';
import Overview from '../Overview';
import Pricing from '../Pricing';
import ReviewsSection from '../ReviewsSection';
import SectionTitle from '../SectionTitle';
import Sidebar from '../Sidebar';
import StyleOnScroll from '../StyleOnScroll';
import UserReviews from '../UserReviews';
import AlternativesSection from './components/AlternativesSection';

import classes from './CompareDesktopLayout.module.scss';

const MAX_NUMBER_OF_COMPARISONS = 4;

const CompareDesktopLayout = () => {
  const {
    listings = [],
    features = [],
    integrations = [],
    alternatives = [],
    comparisonReviews = {},
    title,
  } = useContext(CompareContext);
  const { isCypressTakingSnapshots } = useAppContext();
  const shouldRenderVideoSection = listings.some(
    (listing) => listing.videos && !!listing.videos.length
  );

  const shouldRenderUserVerdicts = listings.every(
    (listing) =>
      listing.video_comparison_reviews &&
      !!listing.video_comparison_reviews.length
  );
  const navSections = generateNavsForUserVerdicts(shouldRenderUserVerdicts);

  const shouldRenderAddToCompare = listings.length < MAX_NUMBER_OF_COMPARISONS;
  const hasComparisonReviews =
    comparisonReviews.first || comparisonReviews.last;
  const labelVariantTitle = 'heading-md';
  const rightSideStyles =
    listings.length > 2 ? classes.threeItemsCompare : classes.twoItemsCompare;

  return (
    <>
      <Container className={classes.container}>
        <TrustBanner />

        <header>
          <Typography
            component="h1"
            variant="heading-xl"
            className={classes.title}
          >
            {title}
          </Typography>
        </header>
        <div className={classes.contentContainer}>
          <div className={clsx(classes.sideMenuContainer)}>
            <div
              className={clsx(classes.menuContainer, {
                [classes.menuSticky]: !isCypressTakingSnapshots,
              })}
            >
              <Sidebar navSections={Object.values(navSections)} />
            </div>
          </div>
          <div className={classes.listingsContainer}>
            {shouldRenderAddToCompare && (
              <div
                className={clsx(classes.rightSideContainer, rightSideStyles)}
              >
                <div
                  className={clsx(classes.similarAppsContainer, {
                    [classes.similarAppsSticky]: !isCypressTakingSnapshots,
                  })}
                >
                  <AddToCompare
                    listings={listings}
                    alternatives={alternatives}
                  />
                </div>
              </div>
            )}
            <div className={classes.listingsWrapper}>
              <StyleOnScroll
                className={clsx(classes.listingTopHeaderWrapper, {
                  [classes.withSticky]: !isCypressTakingSnapshots,
                })}
                onScrollClassName={classes.withBoxShadow}
              >
                <GridContainer>
                  {listings.map((listing, index) => (
                    <GridItem key={`listing-top-header-${listing.id}`}>
                      <ListingTopHeader
                        listingPosition={index}
                        listing={listing}
                        numberOfColumns={listings.length}
                        shouldRenderCloseButton={listings.length > 2}
                      />
                    </GridItem>
                  ))}
                </GridContainer>
              </StyleOnScroll>
              {listings.map((listing, index) => (
                <SoftwareApplicationLD listing={listing} key={index} />
              ))}
              <div
                className="sectionItem"
                name={navSections.overview.id}
                id={navSections.overview.id}
              >
                <SectionTitle
                  label={SECTIONS.overview.name}
                  labelVariant={labelVariantTitle}
                  icon={SECTIONS.overview.icon}
                  columns={listings.length}
                />
                <Overview
                  isCtaFullWidth={true}
                  isCtaTextButton={true}
                  listings={listings}
                />
              </div>
              {shouldRenderUserVerdicts && (
                <div
                  className="sectionItem"
                  name={navSections.verdicts.id}
                  id={navSections.verdicts.id}
                >
                  <SectionTitle
                    label={SECTIONS.verdicts.name}
                    labelVariant={labelVariantTitle}
                    icon={SECTIONS.verdicts.icon}
                    columns={listings.length}
                  />
                  <GridContainer className="UserVerdicts">
                    {listings.map((listing, index) => {
                      const videoReview = listing.video_comparison_reviews[0];

                      return (
                        <>
                          <GridItem
                            className="UserVerdict"
                            key={`${listing.name}-verdict`}
                          >
                            <Paper
                              elevation={0}
                              className={classes.userVerdictContainer}
                            >
                              <EventContext
                                value={{
                                  engagementComponent:
                                    'product_vs_product_video_reviews_card',
                                  name: 'engagement_product_click',
                                }}
                              >
                                <UserVerdict
                                  listing={listing}
                                  videoReview={videoReview}
                                />
                              </EventContext>
                            </Paper>
                          </GridItem>
                          {index === 0 && (
                            <script
                              type="application/ld+json"
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html: getVideoStructuredData({
                                  ...videoReview,
                                  ...videoReview.video_source,
                                  published: videoReview.published_on,
                                }),
                              }}
                            />
                          )}
                        </>
                      );
                    })}
                  </GridContainer>
                </div>
              )}
              <div
                className="sectionItem"
                name={navSections.screenshots.id}
                id={navSections.screenshots.id}
              >
                <SectionTitle
                  label={SECTIONS.screenshots.name}
                  labelVariant={labelVariantTitle}
                  icon={SECTIONS.screenshots.icon}
                  columns={listings.length}
                />
                <GridContainer data-testid="lazy-screenshots">
                  {listings.map((listing) => (
                    <GridItem key={`pricing-content-${listing.id}`}>
                      {shouldRenderVideoSection && (
                        <Paper
                          className={clsx(
                            classes.lazyContentWrapper,
                            listings.length > 2
                              ? classes.lazyContentWrapperMoreThanTwoColumns
                              : classes.lazyContentWrapperUntilTwoColumns
                          )}
                          elevation={0}
                        >
                          <ListingVideos listing={listing} />
                        </Paper>
                      )}
                      <Paper
                        className={clsx(
                          classes.lazyContentWrapper,
                          listings.length > 2
                            ? classes.lazyContentWrapperMoreThanTwoColumns
                            : classes.lazyContentWrapperUntilTwoColumns
                        )}
                        elevation={0}
                      >
                        <EventContext
                          value={{ engagementComponent: 'product_media' }}
                        >
                          <ListingScreenshots
                            listingName={listing.name}
                            screenshots={listing.screenshots}
                          />
                        </EventContext>
                      </Paper>
                    </GridItem>
                  ))}
                </GridContainer>
              </div>
              {!isCypressTakingSnapshots && (
                <div className={classes.serviceForm}>
                  <InlineForm
                    formId="4mPBqrfw206mj7S1JPfygc"
                    gatewayUrl="https://www.capterra.com/bx-capterra-api/bx-forms/submit"
                    formTemplate="INLINE_CARD"
                    headline="Send this comparison chart to my inbox"
                    subHeading=""
                    buttonText="Submit"
                    successMessage="Your request will be delivered to your inbox shortly. If you don't see an email from GetApp in the next 5 minutes, check your spam folder."
                    contextProps={{
                      pageType: 'compare',
                      pagePlacement: 'inline',
                    }}
                    tracking={{
                      formName: 'DMBUY_2022_GA_Blog_Acq',
                      contentCategory: 'Inline Modal',
                      contentRequest: 'Send To Me_Comparison',
                    }}
                  />
                </div>
              )}

              <div
                className="sectionItem"
                name={navSections.pricing.id}
                id={navSections.pricing.id}
              >
                <SectionTitle
                  className={clsx({
                    [classes.noPaddingTop]: !isCypressTakingSnapshots,
                  })}
                  label={SECTIONS.pricing.name}
                  labelVariant={labelVariantTitle}
                  icon={SECTIONS.pricing.icon}
                  columns={listings.length}
                />
                <LazyContent
                  data-testid="lazy-pricing"
                  renderImmediately={true}
                  minHeight={250}
                  columns={listings.length}
                  className={clsx(
                    classes.lazyContentWrapper,
                    listings.length > 2
                      ? classes.lazyContentWrapperMoreThanTwoColumns
                      : classes.lazyContentWrapperUntilTwoColumns
                  )}
                >
                  <Pricing
                    isCtaFullWidth={true}
                    isCtaTextButton={true}
                    listings={listings}
                    columns={listings.length}
                  />
                </LazyContent>
              </div>
              <div
                className="sectionItem"
                name={navSections.reviews.id}
                id={navSections.reviews.id}
              >
                <SectionTitle
                  label={SECTIONS.reviews.name}
                  labelVariant={labelVariantTitle}
                  icon={SECTIONS.reviews.icon}
                  columns={listings.length}
                />
                <LazyContent
                  data-testid="lazy-reviews"
                  renderImmediately={true}
                  minHeight={1000}
                  columns={listings.length}
                  className={clsx(
                    classes.lazyContentWrapper,
                    listings.length > 2
                      ? classes.lazyContentWrapperMoreThanTwoColumns
                      : classes.lazyContentWrapperUntilTwoColumns
                  )}
                >
                  <UserReviews
                    snippetSize="large"
                    listings={listings}
                    isCtaFullWidth={true}
                    isCtaTextButton={true}
                  />
                </LazyContent>
              </div>
              <div
                className="sectionItem"
                name={navSections.features.id}
                id={navSections.features.id}
              >
                <SectionTitle
                  label={SECTIONS.features.name}
                  labelVariant={labelVariantTitle}
                  icon={SECTIONS.features.icon}
                  columns={listings.length}
                />
                <LazyContent
                  data-testid="lazy-features"
                  renderImmediately={true}
                  minHeight={350}
                  columns={listings.length}
                  className={clsx(
                    classes.lazyContentWrapper,
                    listings.length > 2
                      ? classes.lazyContentWrapperMoreThanTwoColumns
                      : classes.lazyContentWrapperUntilTwoColumns
                  )}
                >
                  <FeaturesIntegrations
                    isCtaFullWidth={true}
                    isCtaTextButton={true}
                    listings={listings}
                    section={features}
                    category={SECTIONS.features.slug}
                  />
                </LazyContent>
              </div>
              <div
                className="sectionItem"
                name={navSections.integrations.id}
                id={navSections.integrations.id}
              >
                <SectionTitle
                  label={SECTIONS.integrations.name}
                  labelVariant={labelVariantTitle}
                  icon={SECTIONS.integrations.icon}
                  columns={listings.length}
                />
                <LazyContent
                  data-testid="lazy-integrations"
                  renderImmediately={true}
                  minHeight={350}
                  columns={listings.length}
                  className={clsx(
                    classes.lazyContentWrapper,
                    listings.length > 2
                      ? classes.lazyContentWrapperMoreThanTwoColumns
                      : classes.lazyContentWrapperUntilTwoColumns
                  )}
                >
                  <FeaturesIntegrations
                    isCtaFullWidth={true}
                    isCtaTextButton={true}
                    listings={listings}
                    section={integrations}
                    category={SECTIONS.integrations.slug}
                  />
                </LazyContent>
              </div>
              <div
                className="sectionItem"
                name={navSections.alternatives.id}
                id={navSections.alternatives.id}
              >
                <SectionTitle
                  columns={listings.length}
                  icon="chart-network"
                  label="Alternatives"
                  labelVariant={labelVariantTitle}
                  tagline="Explore similar apps"
                />
                <LazyContent
                  data-testid="lazy-alternatives"
                  renderImmediately={true}
                  minHeight={250}
                  columns={listings.length}
                  className={clsx(
                    classes.lazyContentWrapper,
                    listings.length > 2
                      ? classes.lazyContentWrapperMoreThanTwoColumns
                      : classes.lazyContentWrapperUntilTwoColumns
                  )}
                >
                  <GridContainer>
                    {listings.map((listing) => (
                      <GridItem key={`alternatives-content-${listing.id}`}>
                        <AlternativesSection
                          ctaUrl={listing.cta_urls.alternatives}
                          isCtaFullWidth={true}
                          isCtaTextButton={true}
                          items={listing.alternatives}
                          isTwoCols={listings.length === 2}
                        />
                      </GridItem>
                    ))}
                  </GridContainer>
                </LazyContent>
              </div>
            </div>
          </div>
        </div>
      </Container>
      {hasComparisonReviews && (
        <Container className={classes.container}>
          <ReviewsSection
            title="User reviews that mention these apps"
            reviews={comparisonReviews}
            listings={listings}
            lazy={true}
          />
        </Container>
      )}
      <hr
        className={
          listings.length > 2
            ? classes.emptyDescription
            : classes.hybridEmptyDescription
        }
      />
    </>
  );
};

export default CompareDesktopLayout;
